<template>
  <div class="issue-selection">
    <div class="d-flex align-center flex-wrap pb-2" style="gap: 8px">
      <v-btn small text color="primary" :disabled="loading || !rows.length" @click="selectAll">
        <v-icon left small>mdi-checkbox-multiple-marked-outline</v-icon>
        Select all
      </v-btn>
      <v-btn
        small
        text
        color="primary"
        :disabled="loading || !suggestedKeys.length"
        @click="selectSuggested">
        <v-icon left small>mdi-auto-fix</v-icon>
        Select suggested
      </v-btn>
      <v-btn small text :disabled="loading || !value.length" @click="clearSelection">
        <v-icon left small>mdi-close-circle-outline</v-icon>
        Clear
      </v-btn>
      <v-spacer></v-spacer>
      <span class="text--secondary caption">{{ value.length }} of {{ rows.length }} selected</span>
    </div>

    <v-data-table
      v-model="selectedRows"
      class="elevation-0 rounded-0 custom-table"
      show-select
      item-key="key"
      :headers="headers"
      :items="rows"
      :loading="loading"
      :footer-props="{
        showFirstLastPage: true,
        firstIcon: 'mdi-chevron-double-left',
        lastIcon: 'mdi-chevron-double-right',
        prevIcon: 'mdi-chevron-left',
        nextIcon: 'mdi-chevron-right',
        itemsPerPageText: 'Row/Page',
        itemsPerPageOptions: [15, 30, 50, 100, -1],
      }">
      <template v-slot:[`item.key`]="{ item }">
        <a
          v-if="item.browseUrl"
          :href="item.browseUrl"
          target="_blank"
          rel="noopener"
          class="issue-selection__link d-inline-flex align-center"
          @click.stop>
          {{ item.key }}
          <v-icon x-small class="ml-1">mdi-open-in-new</v-icon>
        </a>
        <span v-else class="font-weight-medium">{{ item.key }}</span>
      </template>

      <template v-slot:[`item.typeText`]="{ item }">
        <span class="text--secondary">{{ item.typeText }}</span>
      </template>

      <template v-slot:[`item.statusText`]="{ item }">
        <span class="text--secondary">{{ item.statusText }}</span>
      </template>

      <template v-slot:[`item.categoryText`]="{ item }">
        <v-chip v-if="item.categoryText" x-small outlined :color="item.categoryColor">
          <v-icon x-small left>{{ item.categoryIcon }}</v-icon>
          {{ item.categoryText }}
        </v-chip>
        <span v-else class="text--disabled">—</span>
      </template>

      <template v-slot:no-data>
        <div class="text-center d-flex flex-column">
          <img width="240" src="/img/notFound.svg" class="py-5 my-8" style="opacity: 0.54" />
          <p class="font-weight-medium">This version has no Jira tickets.</p>
        </div>
      </template>

      <template v-slot:loading>
        <div class="text-center d-flex flex-column">
          <img width="240" src="/img/loading.svg" class="py-5 my-8" style="opacity: 0.54" />
          <p class="font-weight-medium">Loading tickets...</p>
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<script>
  import { ReleaseItemCategoryLabels, releaseItemCategoryMeta } from '@ReleaseNotes/config/enums';
  import { readJiraUrl } from '@ReleaseNotes/config/jiraMeta';

  export default {
    name: 'IssueSelectionTable',
    props: {
      issues: {
        type: Array,
        default: () => [],
      },
      loading: {
        type: Boolean,
        default: false,
      },
      // Selected issue keys — this component is fully controlled and never touches the store.
      value: {
        type: Array,
        default: () => [],
      },
    },
    data() {
      return {
        headers: [
          { text: 'Key', value: 'key', width: '130px' },
          { text: 'Type', value: 'typeText', width: '130px' },
          { text: 'Status', value: 'statusText', width: '130px' },
          { text: 'Summary', value: 'summary' },
          { text: 'Suggested category', value: 'categoryText', width: '170px' },
        ],
      };
    },
    computed: {
      rows() {
        return (this.issues || []).map((issue) => {
          const categoryMeta = releaseItemCategoryMeta(issue.suggestedCategory);
          return {
            ...issue,
            typeText: this.readText(issue, ['type', 'issueType', 'issuetype']),
            statusText: this.readText(issue, ['status']),
            categoryText: ReleaseItemCategoryLabels[issue.suggestedCategory] || '',
            categoryIcon: categoryMeta.icon,
            categoryColor: categoryMeta.color,
            browseUrl: readJiraUrl(issue, issue.key),
          };
        });
      },
      suggestedKeys() {
        return this.rows.filter((row) => row.suggestedInclude).map((row) => row.key);
      },
      selectedRows: {
        get() {
          const selected = new Set(this.value || []);
          return this.rows.filter((row) => selected.has(row.key));
        },
        set(rows) {
          this.emitKeys((rows || []).map((row) => row.key));
        },
      },
    },
    watch: {
      issues: {
        handler() {
          // First load of a version's tickets: honour the AI's `suggestedInclude` hints.
          // A caller that arrives with an existing selection (the Items tab re-picking
          // tickets for a release) keeps it — only an empty selection is pre-filled.
          if ((this.value || []).length) return;
          if (!this.suggestedKeys.length) return;
          this.emitKeys(this.suggestedKeys);
        },
      },
    },
    methods: {
      /**
       * Jira sub-objects arrive either as a plain string or as `{ name }` / `{ value }`.
       * @param {object} issue raw issue
       * @param {string[]} fields candidate field names, in preference order
       * @returns {string}
       */
      readText(issue, fields) {
        for (const field of fields) {
          const raw = issue ? issue[field] : null;
          if (typeof raw === 'string' && raw.trim()) return raw.trim();
          if (raw && typeof raw === 'object') {
            const nested = raw.name || raw.value || raw.text;
            if (typeof nested === 'string' && nested.trim()) return nested.trim();
          }
        }
        return '—';
      },

      emitKeys(keys) {
        this.$emit('input', keys);
      },

      selectAll() {
        this.emitKeys(this.rows.map((row) => row.key));
      },

      selectSuggested() {
        this.emitKeys(this.suggestedKeys);
      },

      clearSelection() {
        this.emitKeys([]);
      },
    },
  };
</script>

<style scoped lang="scss">
  .issue-selection__link {
    color: var(--v-primary-base);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
</style>
